import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UnsavedChangesProvider } from './contexts/UnsavedChangesContext';
import { api } from './lib/api';
import { canSeeInfraFunds } from './lib/access';
import { Toaster } from './components/ui/sonner';
import { Sidebar } from './components/Sidebar';
import { WhatsNewModal } from './components/WhatsNewModal';
import { MondayDigestModal } from './components/MondayDigestModal';
import { PasskeyLock } from './components/PasskeyLock';
import { Menu, PhoneIncoming } from 'lucide-react';
import { InboundCallDialog } from './components/InboundCall';
import { applyTextScale } from './lib/textScale';
import LoginPage from './pages/LoginPage';
import FunnelPage from './pages/FunnelPage';
import SequenceSettingsPage from './pages/SequenceSettingsPage';
import ClaudeConnectPage from './pages/ClaudeConnectPage';
import AiCostsPage from './pages/AiCostsPage';
import VestaboardManagerPage from './pages/VestaboardManagerPage';
import DashboardPage from './pages/DashboardPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import OnsitePlannerPage from './pages/OnsitePlannerPage';
import OnsiteRunnerPage from './pages/OnsiteRunnerPage';
import ScorecardInfoPage from './pages/ScorecardInfoPage';
import ComparisonPage from './pages/ComparisonPage';
import ActivityFeedPage from './pages/ActivityFeedPage';
import BudgetDashboardPage from './pages/BudgetDashboardPage';
import RubriqueDetailPage from './pages/RubriqueDetailPage';
import ReportingPage from './pages/ReportingPage';
import TeamPage from './pages/TeamPage';
import SettingsPage from './pages/SettingsPage';
import PendingReviewsPage from './pages/PendingReviewsPage';
import WhosTurnPage from './pages/WhosTurnPage';
import ExplorerPage from './pages/ExplorerPage';
import ThesisScreeningPage from './pages/ThesisScreeningPage';
import InfraFundsPage from './pages/InfraFundsPage';
import InfraFundDetailPage from './pages/InfraFundDetailPage';
import SectorsPage from './pages/SectorsPage';
import TeamMeetingPage from './pages/TeamMeetingPage';
import ACMeetingPage from './pages/ACMeetingPage';
import TeamDevelopmentPage from './pages/TeamDevelopmentPage';
import TeamKpisPage from './pages/TeamKpisPage';
import OutreachPage from './pages/OutreachPage';
import ContactsPage from './pages/ContactsPage';
import CampaignsPage from './pages/CampaignsPage';
import BrokerTrafficPage from './pages/BrokerTrafficPage';
import DealInboxPage from './pages/DealInboxPage';
import PublicNdaPage from './pages/PublicNdaPage';
import BookmarksPage from './pages/BookmarksPage';
import FavoritesPage from './pages/FavoritesPage';
import MyTimePage from './pages/MyTimePage';
import PipelineHealthPage from './pages/PipelineHealthPage';
import SystemPage from './pages/SystemPage';
import MentionsPage from './pages/MentionsPage';
import TeamAnalyticsPage from './pages/TeamAnalyticsPage';
import CallAnalyticsPage from './pages/CallAnalyticsPage';
import BrokerCallsPage from './pages/BrokerCallsPage';
import ApprovalWorkplacePage from './pages/ApprovalWorkplacePage';
import PostPage from './pages/PostPage';
import MyTasksPage from './pages/MyTasksPage';
import UserManualPage from './pages/UserManualPage';
import OutreachGuidePage from './pages/OutreachGuidePage';
import FeedbackGuidePage from './pages/FeedbackGuidePage';
import PublicLandingPage from './pages/PublicLandingPage';
import TestDevicePage from './pages/TestDevicePage';
import ACFeedbackPage from './pages/ACFeedbackPage';
import OutreachStatsPage from './pages/OutreachStatsPage';
import CleanOutreachStatsPage from './pages/CleanOutreachStatsPage';
import BrokerStatsPage from './pages/BrokerStatsPage';
import AbTestLivePage from './pages/AbTestLivePage';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return children;
}

function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to={location.state?.from || '/'} replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function NonAcRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (user.role === 'ac_member') return <Navigate to="/" replace />;
  return children;
}

function InfraRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (!canSeeInfraFunds(user)) return <Navigate to="/" replace />;
  return children;
}

function TeamDevRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (user.role === 'ac_member' && !user.team_dev_access) return <Navigate to="/" replace />;
  return children;
}

function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [inboundOpen, setInboundOpen] = useState(false);
  const { user } = useAuth();
  const nonAc = user && user.role !== 'ac_member';
  const location = useLocation();
  const lastView = useRef({ path: '', t: 0 });
  useEffect(() => {
    const p = location.pathname;
    if (p === lastView.current.path && Date.now() - lastView.current.t < 30000) return;
    lastView.current = { path: p, t: Date.now() };
    api.trackPageview({ path: p }).catch(() => {});
  }, [location.pathname]);
  useEffect(() => {
    let lastActivity = Date.now();
    const markActive = () => { lastActivity = Date.now(); };
    const activityEvents = ['mousedown', 'keydown', 'mousemove', 'wheel', 'touchstart', 'scroll'];
    activityEvents.forEach((e) => window.addEventListener(e, markActive, { passive: true }));
    const beat = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivity > 5 * 60 * 1000) return;
      api.heartbeat().catch(() => {});
    };
    beat();
    const iv = setInterval(beat, 60000);
    document.addEventListener('visibilitychange', beat);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', beat);
      activityEvents.forEach((e) => window.removeEventListener(e, markActive));
    };
  }, []);
  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      {/* iOS status bar mask (PWA safe area) */}
      <div className="fixed inset-x-0 top-0 z-[45] lg:hidden bg-background" style={{ height: 'env(safe-area-inset-top)' }} />
      {/* Mobile hamburger */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="fixed top-[calc(env(safe-area-inset-top)+0.875rem)] left-3 z-40 lg:hidden bg-card border border-border rounded-sm p-2 text-foreground"
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-5 h-5" />
      </button>
      {/* Mobile inbound-call button — instant caller briefing on the road */}
      {nonAc && (
        <button
          onClick={() => setInboundOpen(true)}
          className="fixed top-[calc(env(safe-area-inset-top)+0.875rem)] right-3 z-40 lg:hidden bg-emerald-950/90 border border-emerald-500/40 rounded-sm p-2 text-emerald-300"
          data-testid="mobile-inbound-call-btn"
          title="Inbound Call"
        >
          <PhoneIncoming className="w-5 h-5" />
        </button>
      )}
      {nonAc && <InboundCallDialog open={inboundOpen} onClose={() => setInboundOpen(false)} />}
      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 lg:relative lg:h-dvh lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <Sidebar onNavigate={() => setSidebarOpen(false)} />
      </div>
      <main className="flex-1 p-4 pt-[calc(env(safe-area-inset-top)+3.5rem)] pb-[calc(env(safe-area-inset-bottom)+1rem)] lg:p-6 overflow-y-auto overflow-x-hidden min-w-0">
        {children}
      </main>
      <WhatsNewModal />
      <MondayDigestModal />
      <PasskeyLock />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
      <Route path="/claude-connect" element={<ProtectedRoute><ClaudeConnectPage /></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><AppLayout><FunnelPage /></AppLayout></ProtectedRoute>} />
      <Route path="/pipeline" element={<ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/projects/:id" element={<ProtectedRoute><AppLayout><ProjectDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/projects/:id/onsite/:planId" element={<NonAcRoute><AppLayout><OnsitePlannerPage /></AppLayout></NonAcRoute>} />
      <Route path="/onsite-run/:planId/:guideId" element={<NonAcRoute><OnsiteRunnerPage /></NonAcRoute>} />
      <Route path="/scorecard-info" element={<ProtectedRoute><AppLayout><ScorecardInfoPage /></AppLayout></ProtectedRoute>} />
      <Route path="/compare" element={<NonAcRoute><AppLayout><ComparisonPage /></AppLayout></NonAcRoute>} />
      <Route path="/tasks" element={<NonAcRoute><AppLayout><MyTasksPage /></AppLayout></NonAcRoute>} />
      <Route path="/activity" element={<ProtectedRoute><AppLayout><ActivityFeedPage /></AppLayout></ProtectedRoute>} />
      <Route path="/budget" element={<NonAcRoute><AppLayout><BudgetDashboardPage /></AppLayout></NonAcRoute>} />
      <Route path="/budget/rubrique/:rubriqueId" element={<NonAcRoute><AppLayout><RubriqueDetailPage /></AppLayout></NonAcRoute>} />
      <Route path="/reporting" element={<NonAcRoute><AppLayout><ReportingPage /></AppLayout></NonAcRoute>} />
      <Route path="/meeting" element={<NonAcRoute><AppLayout><TeamMeetingPage /></AppLayout></NonAcRoute>} />
      <Route path="/ac-meeting" element={<NonAcRoute><AppLayout><ACMeetingPage /></AppLayout></NonAcRoute>} />
      <Route path="/team-development" element={<TeamDevRoute><AppLayout><TeamDevelopmentPage /></AppLayout></TeamDevRoute>} />
      <Route path="/team-kpis" element={<AppLayout><TeamKpisPage /></AppLayout>} />
      <Route path="/outreach" element={<NonAcRoute><AppLayout><OutreachPage /></AppLayout></NonAcRoute>} />
      <Route path="/outreach-sequences" element={<NonAcRoute><AppLayout><SequenceSettingsPage /></AppLayout></NonAcRoute>} />
      <Route path="/contacts" element={<NonAcRoute><AppLayout><ContactsPage /></AppLayout></NonAcRoute>} />
      <Route path="/broker-campaigns" element={<NonAcRoute><AppLayout><CampaignsPage /></AppLayout></NonAcRoute>} />
      <Route path="/broker-traffic" element={<NonAcRoute><AppLayout><BrokerTrafficPage /></AppLayout></NonAcRoute>} />
      <Route path="/deal-inbox" element={<NonAcRoute><AppLayout><DealInboxPage /></AppLayout></NonAcRoute>} />
      <Route path="/outreach-stats" element={<ProtectedRoute><AppLayout><OutreachStatsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/outreach-stats/clean" element={<ProtectedRoute><AppLayout><CleanOutreachStatsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/ab-live" element={<ProtectedRoute><AppLayout><AbTestLivePage /></AppLayout></ProtectedRoute>} />
      <Route path="/outreach-guide" element={<NonAcRoute><AppLayout><OutreachGuidePage /></AppLayout></NonAcRoute>} />
      <Route path="/feedback-guide" element={<NonAcRoute><AppLayout><FeedbackGuidePage /></AppLayout></NonAcRoute>} />
      <Route path="/team" element={<AdminRoute><AppLayout><TeamPage /></AppLayout></AdminRoute>} />
      <Route path="/team-analytics" element={<AdminRoute><AppLayout><TeamAnalyticsPage /></AppLayout></AdminRoute>} />
      <Route path="/calls" element={<NonAcRoute><AppLayout><CallAnalyticsPage /></AppLayout></NonAcRoute>} />
      <Route path="/broker-calls" element={<NonAcRoute><AppLayout><BrokerCallsPage /></AppLayout></NonAcRoute>} />
      <Route path="/broker-stats" element={<NonAcRoute><AppLayout><BrokerStatsPage /></AppLayout></NonAcRoute>} />
      <Route path="/approval-workplace" element={<NonAcRoute><AppLayout><ApprovalWorkplacePage /></AppLayout></NonAcRoute>} />
      <Route path="/post" element={<NonAcRoute><AppLayout><PostPage /></AppLayout></NonAcRoute>} />
      <Route path="/sectors" element={<AdminRoute><AppLayout><SectorsPage /></AppLayout></AdminRoute>} />
      <Route path="/settings" element={<ProtectedRoute><AppLayout><SettingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/reviews" element={<ProtectedRoute><AppLayout><PendingReviewsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/whos-turn" element={<NonAcRoute><AppLayout><WhosTurnPage /></AppLayout></NonAcRoute>} />
      <Route path="/explorer" element={<NonAcRoute><AppLayout><ExplorerPage /></AppLayout></NonAcRoute>} />
      <Route path="/thesis-screening" element={<AdminRoute><AppLayout><ThesisScreeningPage /></AppLayout></AdminRoute>} />
      <Route path="/infra-funds" element={<InfraRoute><AppLayout><InfraFundsPage /></AppLayout></InfraRoute>} />
      <Route path="/infra-funds/:id" element={<InfraRoute><AppLayout><InfraFundDetailPage /></AppLayout></InfraRoute>} />
      <Route path="/bookmarks" element={<ProtectedRoute><AppLayout><BookmarksPage /></AppLayout></ProtectedRoute>} />
      <Route path="/favorites" element={<ProtectedRoute><AppLayout><FavoritesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/my-time" element={<ProtectedRoute><AppLayout><MyTimePage /></AppLayout></ProtectedRoute>} />
      <Route path="/notifications" element={<Navigate to="/system" replace />} />
      <Route path="/health" element={<NonAcRoute><AppLayout><PipelineHealthPage /></AppLayout></NonAcRoute>} />
      <Route path="/mentions" element={<NonAcRoute><AppLayout><MentionsPage /></AppLayout></NonAcRoute>} />
      <Route path="/system" element={<AdminRoute><AppLayout><SystemPage /></AppLayout></AdminRoute>} />
      <Route path="/ai-costs" element={<AdminRoute><AppLayout><AiCostsPage /></AppLayout></AdminRoute>} />
      <Route path="/vestaboard" element={<AdminRoute><AppLayout><VestaboardManagerPage /></AppLayout></AdminRoute>} />
      <Route path="/manual" element={<NonAcRoute><AppLayout><UserManualPage /></AppLayout></NonAcRoute>} />
      <Route path="/m/:slug" element={<PublicLandingPage />} />
      <Route path="/test-geraet" element={<TestDevicePage />} />
      <Route path="/nda/:token" element={<PublicNdaPage />} />
      <Route path="/ac-feedback" element={<ACFeedbackPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => { applyTextScale(); }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <UnsavedChangesProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </UnsavedChangesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
